import React, { memo, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { geoCentroid } from "d3-geo";
import { scaleQuantize } from "d3-scale";
import { useTranslation } from "react-i18next";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { ReactComponent as PortugalMainland } from "./portugal_mainland_silhouette_clean.svg";

const geoUrl = "/assets/data/geoman.geojson";

type Props = {
  values: number[]; // valores por codigo, [0] = total, 1..5 = regiones
  className?: string;
  setTooltipContent?: (content: string) => void;
  loading?: boolean;
};

const colorScaleP = scaleLinear<string>()
  .domain([0, 100])
  .range(["#5CC6CE", "#0B3E45"]); // claro → oscuro (teal, ESP)

const colorScaleN = scaleLinear<string>()
  .domain([-100, 0])
  .range(["#3F3A39", "#BEB8B5"]); // oscuro → claro (gris-marrón, ESP)

// Opcional: ajustar coordenadas si usas labels/marcadores
const getLabelCoord = (codigo: number): [number, number] => {
  switch (codigo) {
    case 1:
      return [-9.1, 38.8]; // Lisboa
    case 2:
      return [-8.61, 41.16]; // Área Metropolitana do Porto
    case 3:
      return [-7.75, 41.4]; // Norte
    case 4:
      return [-7.9, 40.4]; // Centro
    case 5:
      return [-8, 38.2]; // Alenteho
    case 6:
      return [-8.16, 37.15]; // Sul e Ilhas (posición aproximada en continente)
    default:
      return [0, 0];
  }
};

const MapChartPT = ({
  setTooltipContent,
  values,
  className,
  loading,
}: {
  setTooltipContent: any;
  values: number[];
  className?: string;
  loading?: boolean;
}) => {
  const { i18n } = useTranslation();
  const colorScaleP = scaleQuantize<string>()
    .domain([0, Math.max(...values)])
    .range([
      "#bbdee0",
      "#add9dc",
      "#9fd5d9",
      "#8fd1d6",
      "#80cdd4",
      "#70cad2",
      "#5fc7d1",
      "#4ec4d1",
      "#3cc1d0",
      "#2dbcce",
      "#26b1c3",
      "#20a5b8",
      "#1a98ab",
      "#158c9f",
      "#117f91",
      "#0d7284",
      "#096475",
      "#065766",
      "#044957",
      "#023b47",
    ]);
  const colorScaleN = scaleQuantize<string>()
    .domain([Math.min(...values), 0])
    .range([
      "#393939",
      "#414141",
      "#4a4a4a",
      "#525252",
      "#5b5b5b",
      "#636363",
      "#6c6c6c",
      "#747474",
      "#7c7c7c",
      "#858585",
      "#8d8d8d",
      "#969696",
      "#9e9e9e",
      "#a6a6a6",
      "#afafaf",
      "#b7b7b7",
      "#c0c0c0",
      "#c8c8c8",
      "#d1d1d1",
      "#d9d9d9",
    ]);

  return (
    <div style={{ display: "flex", flexDirection: "column", }}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 3000,
          center: [-8, 39.5],
        }}
        width={650}
        height={380}
        style={{ width: "100%", height: "auto" }}
      >
        <Geographies geography={geoUrl}>
          {({ geographies }: { geographies: Array<any> }) => (
            <>
              {geographies.map((geo: any) => {
                const { codigo } = geo.properties;
                const val = values[codigo] || 0;
                const fill = val >= 0 ? colorScaleP(val) : colorScaleN(val);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    onMouseEnter={() => {
                      const ccaa =
                        i18n.language === "es"
                          ? geo.properties.ccaa
                          : geo.properties.ccaaEn;

                      if (!loading)
                        setTooltipContent(
                          `${ccaa}: ${values[geo.properties.codigo]}%`
                        );
                    }}
                    onMouseLeave={() => {
                      setTooltipContent("");
                    }}
                    style={{
                      default: {
                        outline: "none",
                        stroke: "#191A1B",
                        paintOrder: "stroke",
                        strokeWidth: 0.05,
                        fillOpacity: 0.9,
                      },
                      hover: {
                        fillOpacity: 1,
                        outline: "none",
                        stroke: "#191A1B",
                        paintOrder: "fill",
                        strokeWidth: 0,
                        cursor: "pointer",
                      },
                      pressed: {
                        fillOpacity: 0.5,
                        outline: "none",
                      },
                    }}
                  />
                );
              })}
              {!loading &&
                geographies.map((geo: any) => {
                  const codigo = geo.properties.codigo as number;
                  const val = values?.[codigo] ?? 0;
                  return (
                    <g key={codigo + "-name"}>
                      <Marker coordinates={getLabelCoord(codigo)}>
                        <text
                          fontSize={8}
                          fontWeight={700}
                          pointerEvents="none"
                          paintOrder="stroke"
                          textAnchor="middle"
                          strokeWidth={1.5}
                          fill="white"
                          stroke="#000"
                        >
                          {`${Math.round(val * 10) / 10}%`}
                        </text>
                      </Marker>
                    </g>
                  );
                })}
            </>
          )}
        </Geographies>
      </ComposableMap>

      {/* Bloque Total */}
      <div
        className="region-total"
        style={{
          marginTop: "0.5rem",
          marginRight: "0.5rem",
          textAlign: "right",
          alignSelf: "flex-end",
          pointerEvents: "none",
        }}
      >
        <PortugalMainland
          fill="rgb(78, 196, 209)"
          style={{
            transform: "scale(7.5)",
            width: 120,
            height: "auto",
            marginBottom: 8,
          }}
        />
        <div>
          <strong>Total:</strong> {values?.[0] ?? 0}%
        </div>
      </div>
    </div>
  );
};

export default memo(MapChartPT);
